import { FC } from 'react';
import { AttachFile } from '@mui/icons-material';
import { CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import GradientAiIcon from 'components/icons/GradientAiIcon';
import { Column, Row } from 'further-ui/components/Layout';
import React from 'react';
import { ChatMessageRole } from 'further-types/lp/ai-agent';
import { ChatMessage } from '../types';
import EmptyState from './EmptyState';
import AgentResponse from './AgentResponse';
import Timestamp from './Timestamp';

const MessagesArea = styled(Column)(({ theme }) => ({
  overflowY: 'auto',
  padding: theme.spacing(5),
  paddingBottom: theme.spacing(2),
  flex: 1,
  minHeight: 0,
  userSelect: 'text',
}));

const UserCard = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(3, 4),
  maxWidth: '88%',
  alignSelf: 'flex-end',
  color: theme.palette.grey[900],
}));

const FileDivider = styled('div')(({ theme }) => ({
  margin: theme.spacing(2, 0),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const ActionRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(0.25, 0),
}));

type Props = {
  messages: Array<ChatMessage>;
  isLoading: boolean;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onSend: (text: string) => void;
  onAttachClick: () => void;
  hasInput: boolean;
};

const isEmptyState = (messages: Array<ChatMessage>, isLoading: boolean) =>
  messages.length === 0 && !isLoading;

const MessageList: FC<Props> = ({
  messages,
  isLoading,
  messagesEndRef,
  onSend,
  onAttachClick,
  hasInput,
}) => {
  return (
    <MessagesArea spacing="lg">
      {isEmptyState(messages, isLoading) && (
        <EmptyState onSend={onSend} showPresets={!hasInput} />
      )}

      {messages.map((message, index) => {
        if (message.role === ChatMessageRole.User) {
          return (
            <Column key={index} spacing="sm">
              <UserCard>
                <Typography
                  variant="body2"
                  color="grey.900"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {message.content}
                </Typography>
                {message.fileName && (
                  <>
                    <FileDivider />
                    <Row centered spacing="xs">
                      <AttachFile sx={{ fontSize: 16 }} />
                      <Typography variant="body2">
                        {message.fileName}
                      </Typography>
                    </Row>
                  </>
                )}
              </UserCard>
              <Timestamp date={message.timestamp} align="right" />
            </Column>
          );
        }

        if (message.role === ChatMessageRole.Action) {
          return (
            <ActionRow key={index} justify="space-between" centered>
              <Row centered spacing="xs">
                <GradientAiIcon size={16} />
                <Typography variant="body2" color="text.disabled">
                  {message.content}
                </Typography>
              </Row>
            </ActionRow>
          );
        }

        // If not user or action, its the assistant. Render segments in the order Claude returned them
        if (!message.segments.length) return null;

        return (
          <AgentResponse
            key={index}
            message={message}
            onAttachClick={onAttachClick}
          />
        );
      })}

      {isLoading && (
        <Row centered spacing="xs" justify="space-between">
          <Row spacing="xs" centered>
            <GradientAiIcon grey />
            <Typography variant="body2" color="grey.500">
              Thinking...
            </Typography>
          </Row>
          <CircularProgress size={16} sx={{ color: 'grey.900' }} />
        </Row>
      )}

      {!isEmptyState(messages, isLoading) ? <div ref={messagesEndRef} /> : null}
    </MessagesArea>
  );
};

export default MessageList;
